import * as React from 'react';
import { Theme, ThemeProvider } from '@mui/material/styles';
import { observer } from 'mobx-react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import { breakpointObserver, BreakpointObserverInfo, useBreakpointInfo } from './../Helper/WindowSize';
import { layoutInformation } from '../Helper/Darkmode';

import { setLayoutDimension } from '@egr/xbox/telemetry/TrackingUtils';

interface OverrideableAppThemeContextProps {
    disableMuiTooltips: boolean;
    disableLazyLoadImage: boolean;
    useBottomSheet: boolean;
    hideLogoInArticleList: boolean;
    newSettingsDesign: boolean;
    greyGDIcons: boolean;
    thinIcons: boolean;
}

export interface AppThemeContextProps extends OverrideableAppThemeContextProps {
    smallLayout: boolean;
    phoneLandscape: boolean;
    darkmode: boolean;
    imagePlaceholder: React.ReactNode;
    loadingImagePlaceholder: React.ReactNode;
    productPlaceholder: React.ReactNode;
    projectPlaceholder: React.ReactNode;
}

export const AppThemeContextDefault: AppThemeContextProps = {
    disableMuiTooltips: false,
    disableLazyLoadImage: false,
    smallLayout: false,
    phoneLandscape: false,
    darkmode: false,
    imagePlaceholder: null,
    loadingImagePlaceholder: null,
    productPlaceholder: null,
    projectPlaceholder: null,
    useBottomSheet: false,
    hideLogoInArticleList: false,
    newSettingsDesign: false,
    greyGDIcons: false,
    thinIcons: false,
};

export const AppThemeContext: React.Context<AppThemeContextProps> =
    React.createContext<AppThemeContextProps>(AppThemeContextDefault);

export interface AppThemeComponents {
    imagePlaceholder?: React.ReactNode;
    loadingImagePlaceholder?: React.ReactNode;
    productPlaceholder?: React.ReactNode;
    projectPlaceholder?: React.ReactNode;
}

type ThemeProviderProps = {
    darkTheme: Theme;
    lightTheme: Theme;
} & Partial<OverrideableAppThemeContextProps> & AppThemeComponents;

const muiCache = createCache({
    key: 'mui',
    prepend: true
});

muiCache.compat = true; // disabled SSR-Warnings

export const AppThemeProvider: React.FC<ThemeProviderProps> = observer(
    (props: React.PropsWithChildren<ThemeProviderProps>) => {
        const breakpointInfo: BreakpointObserverInfo = useBreakpointInfo();

        React.useEffect(
            () => {
                const onDarkmodeChange: (value: boolean) => void = (value) => {
                    setLayoutDimension(value);
                    breakpointObserver.setBreakpoints((value ? props.darkTheme : props.lightTheme).breakpoints);
                };
                // set correct initial breakpoints
                onDarkmodeChange(layoutInformation.isDarkmodeActive);
                layoutInformation.onDarkmodeChange.addListener(onDarkmodeChange);
                return layoutInformation.onDarkmodeChange.removeListener(onDarkmodeChange);
            },
            [
                props.darkTheme,
                props.lightTheme,
            ]
        );

        const activeTheme: Theme = layoutInformation.isDarkmodeActive ? props.darkTheme : props.lightTheme;

        return (
            <AppThemeContext.Provider
                value={{
                    darkmode: layoutInformation.isDarkmodeActive,
                    smallLayout: breakpointInfo.smallLayout,
                    phoneLandscape: breakpointInfo.phoneLandscape,
                    disableMuiTooltips: props.disableMuiTooltips ?? false,
                    imagePlaceholder: props.imagePlaceholder ?? null,
                    loadingImagePlaceholder: props.loadingImagePlaceholder ?? null,
                    productPlaceholder: props.productPlaceholder ?? null,
                    projectPlaceholder: props.projectPlaceholder ?? null,
                    useBottomSheet: props.useBottomSheet ?? false,
                    hideLogoInArticleList: props.hideLogoInArticleList ?? false,
                    newSettingsDesign: props.newSettingsDesign ?? false,
                    greyGDIcons: props.greyGDIcons ?? false,
                    disableLazyLoadImage: props.disableLazyLoadImage ?? false,
                    thinIcons: props.thinIcons ?? false
                }}
            >
                <CacheProvider value={muiCache}>
                    <ThemeProvider theme={activeTheme}>
                        {props.children}
                    </ThemeProvider>
                </CacheProvider>
            </AppThemeContext.Provider>
        );
    }
);